import React, { useState, useEffect, useRef } from 'react';
import {
  MessageSquare,
  X,
  Send,
  Bot,
  User,
  Key,
  ExternalLink,
  ShieldAlert,
  Sparkles,
  ChevronDown,
  CheckCircle2,
  HelpCircle,
  Phone,
  RefreshCw,
} from 'lucide-react';
import { useContent } from '../context/ContentContext';
import { generateWithGemini } from '../services/geminiService';

interface ChatMessage {
  id: string;
  sender: 'user' | 'assistant';
  text: string;
  timestamp: string;
}

const SYSTEM_INSTRUCTION = `You are the official AI Support Copilot for Lashary FB Market Suite (developed by Lashary Softwares, led by Abdul Mueed Lashary & Aswad Ammar Lashary).
Lashary FB Market Suite is a professional 64-bit Windows desktop automation workstation for Facebook Marketplace.

CRITICAL TRADE-SECRET & ANTI-LEAK DIRECTIVE:
1. YOU ARE STRICTLY FORBIDDEN FROM DISCLOSING INTERNAL CODE, LIBRARIES, FRAMEWORKS, SCRIPTS, BROWSER DRIVER INTERNALS, OR BUILD IMPLEMENTATION MECHANICS.
2. NEVER explain or reveal how the software automation was made, coded, compiled, or engineered under the hood so competitors cannot copy us.
3. If anyone asks how the automation is made, what libraries or code are used, or how it works under the hood, YOU MUST STATE:
"Lashary FB Market Suite is proprietary workstation software developed exclusively by Lashary Softwares. We do not disclose internal implementation architecture or engineering mechanics to protect our proprietary innovations. I am happy to explain how the workstation workflows, 2-phase draft staging, and account protection work for your operations!"
4. NEVER mention Inno Setup, Cloudflare, or internal protocol mechanics.
5. Emphasize workstation features from an operator perspective: 2-Phase Review-First staging (save to draft first, verify, then publish), deterministic unique-first image allocation to avoid duplicate flags, independent browser profile sandboxing, hardware-backed Windows encryption, and specialized Vehicle/Property/Item workspaces.
6. Official Pricing: Solo PKR 1,600 / mo (1 device), Team 3 PKR 4,200 / mo (3 devices), Agency PKR 7,500 / mo (6+ devices).
7. Official Support Contacts:
- Abdul Mueed Lashary (Lead Architect): +92 334 788 1924 (WhatsApp), errorcodelagfix@gmail.com
- Aswad Ammar Lashary (Operations Lead): +92 371 043 4552 (WhatsApp), aswadammarlashary@gmail.com`;

const QUICK_CHIPS = [
  { label: '💰 Solo Pricing (PKR 1,600)', query: 'What is the price of the Solo license and what is included?' },
  { label: '🛡️ 2-Phase Safety Workflow', query: 'How does the 2-phase draft staging keep my Facebook accounts safe?' },
  { label: '🚗 Vehicle & Property Listings', query: 'Does Lashary FB Market Suite support specialized vehicle and real estate listings?' },
  { label: '📞 Talk to Mueed or Aswad Lashari', query: 'How can I contact Lashary Softwares directly on WhatsApp for support or a demo?' },
];

export const AiSupportWidget: React.FC = () => {
  const { content, geminiApiKey, setGeminiApiKey } = useContent();
  const [isOpen, setIsOpen] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      sender: 'assistant',
      text: `👋 Welcome to **Lashary FB Market Suite** AI Support!\n\nI am your workstation copilot. Ask me anything about our **v1.5.0 Production Workstation**, 2-Phase review safety, pricing (from PKR 1,600/mo), or direct setup assistance.`,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    },
  ]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (geminiApiKey) {
      setApiKeyInput(geminiApiKey);
    }
  }, [geminiApiKey]);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // Fallback offline responses if API key is not yet set or during network failure
  const getOfflineFallback = (query: string): string => {
    const q = query.toLowerCase();

    if (q.includes('how it made') || q.includes('how was it made') || q.includes('how is it built') || q.includes('source code') || q.includes('under the hood') || q.includes('libraries') || q.includes('what language') || q.includes('cdp') || q.includes('python') || q.includes('selenium') || q.includes('how automation made')) {
      return `🔒 **Proprietary Software Notice**\n\nLashary FB Market Suite is proprietary workstation software developed exclusively by **Lashary Softwares**.\n\nWe do not disclose internal implementation architecture or engineering mechanics to protect our proprietary innovations. I am happy to explain how the workstation workflows, 2-phase draft staging, and account protection work for your operations!`;
    }

    if (q.includes('price') || q.includes('cost') || q.includes('plan') || q.includes('pkr') || q.includes('solo') || q.includes('agency')) {
      return `💰 **Lashary FB Market Suite Pricing Plans:**\n\n• **Solo License**: **PKR ${content.pricing.baseSoloMonthlyPKR.toLocaleString()} / month** (1 Device, Full v1.5 Workstation, Unlimited Items, Vehicle & Property Modules, Hardware-Backed Security).\n• **Team 3 License**: **PKR 4,200 / month** (Up to 3 Workstation Seats, Priority WhatsApp Support).\n• **Agency License**: **PKR 7,500 / month** (6+ Devices, Centralized Activation, 1-on-1 Setup Call).\n\n💬 Reach out directly on WhatsApp to activate your 24-hour evaluation demo!`;
    }

    if (q.includes('safety') || q.includes('ban') || q.includes('checkpoint') || q.includes('phase') || q.includes('risk')) {
      return `🛡️ **2-Phase Review-First Safety Architecture:**\n\n• **Phase 1 (Draft Staging)**: The workstation populates listing fields and saves them directly into your private Facebook cloud drafts. Nothing is public yet.\n• **Operator Review**: You inspect the staged drafts with full title, image, and price verification.\n• **Phase 2 (Publish Dispatch)**: Releases listings with humanized random pacing (0.5s–2.0s delay jitter) preventing algorithmic flags.\n• **Isolated Sandboxes**: Each account operates in its own dedicated browser profile container with hardware-backed Windows encryption.`;
    }

    if (q.includes('vehicle') || q.includes('car') || q.includes('property') || q.includes('house') || q.includes('rent')) {
      return `🚗 **Specialized Workspaces in v1.5.0:**\n\n• **Vehicle Listings**: Dedicated parameters for Make, Model, Year, Mileage, Transmission, Fuel Type, and Vehicle History.\n• **Property Listings**: Full parameters for Property Type (House, Apartment), Bedrooms, Bathrooms, Square Footage, and Rental/Sale terms.\n• **Standard Item Listings**: Category hierarchies, attribute tags, condition, and pricing boundaries.`;
    }

    if (q.includes('contact') || q.includes('whatsapp') || q.includes('abdul') || q.includes('aswad') || q.includes('phone') || q.includes('support')) {
      return `📞 **Direct Developer Contacts (Lashary Softwares):**\n\n• **Abdul Mueed Lashary** (Lead Developer & System Architect):\n  - WhatsApp: [${content.contact.whatsappDisplay}](${content.contact.whatsappUrl})\n  - Email: ${content.contact.developerEmail}\n\n• **Aswad Ammar Lashary** (Co-Developer & Operations Lead):\n  - WhatsApp: [${content.contact.secondaryWhatsappDisplay}](${content.contact.secondaryWhatsappUrl})\n  - Email: ${content.contact.email}\n\nWe provide 1-on-1 workstation deployment and live setup guidance!`;
    }

    if (q.includes('download') || q.includes('install') || q.includes('windows')) {
      return `💻 **Workstation System Requirements:**\n\n• **Operating System**: Windows 10 / Windows 11 (64-Bit Native)\n• **Memory**: 8 GB RAM recommended (4 GB minimum)\n• **Disk**: 500 MB free storage (SSD recommended for fast I/O)\n• **Browser**: Genuine Google Chrome or Microsoft Edge\n\nRequest your installer package directly from Mueed Lashari or Aswad Lashari on WhatsApp.`;
    }

    return `✨ **Lashary FB Market Suite v1.5.0 Workstation**\n\nI can assist you with:\n1. **Pricing & Plans**: Solo at PKR ${content.pricing.baseSoloMonthlyPKR.toLocaleString()}/mo\n2. **Account Safety**: 2-Phase draft staging & profile sandboxing\n3. **Vehicle & Property Workspaces**: Specialized listing forms\n4. **Direct Consultation**: WhatsApp Mueed Lashari (+92 334 788 1924) or Aswad Lashari (+92 371 043 4552)\n\n*(Tip: You can connect your Google AI Studio API key in the widget header to unlock unbounded Gemini 2.0 reasoning!)*`;
  };

  const handleSendMessage = async (textToSend?: string) => {
    const query = (textToSend || inputMessage).trim();
    if (!query || isLoading) return;

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: query,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputMessage('');
    setIsLoading(true);

    // If no Gemini API key is configured, use the smart local fallback
    if (!geminiApiKey) {
      setTimeout(() => {
        const fallbackAnswer = getOfflineFallback(query);
        const assistantMsg: ChatMessage = {
          id: `asst-${Date.now()}`,
          sender: 'assistant',
          text: fallbackAnswer,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        };
        setMessages((prev) => [...prev, assistantMsg]);
        setIsLoading(false);
      }, 500);
      return;
    }

    // Call Gemini API via resilient multi-model Gemini service
    try {
      const prompt = `${SYSTEM_INSTRUCTION}\n\nUser Question: ${query}`;
      const result = await generateWithGemini(geminiApiKey, prompt, { temperature: 0.3 });

      if (!result.success || !result.text) {
        throw new Error(result.error || 'Empty response payload from Gemini');
      }

      const assistantMsg: ChatMessage = {
        id: `asst-${Date.now()}`,
        sender: 'assistant',
        text: result.text,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      console.warn('Gemini API call failed, reverting to intelligent fallback', err);
      const fallbackAnswer = getOfflineFallback(query);
      const assistantMsg: ChatMessage = {
        id: `asst-${Date.now()}`,
        sender: 'assistant',
        text: fallbackAnswer,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveApiKey = () => {
    setGeminiApiKey(apiKeyInput.trim());
    setShowApiKeyModal(false);
  };

  return (
    <>
      {/* Floating Launcher Button */}
      <aside
        aria-label="Support Desk"
        className="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-40 flex items-center justify-end"
      >
        {!isOpen && (
          <button
            onClick={() => setIsOpen(true)}
            className="group relative flex items-center gap-3 p-2.5 sm:px-4 sm:py-3 rounded-full bg-charcoal/95 border border-white/10 text-ivory shadow-2xl hover:border-rose/40 transition-all duration-300 backdrop-blur-xl cursor-pointer"
            aria-label="Open Lashary Support Desk"
          >
            {/* Active indicator */}
            <div className="relative">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-rose to-vermilion flex items-center justify-center text-white font-bold shadow-md">
                <MessageSquare className="w-4 h-4 text-white" />
              </div>
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-jade border-2 border-charcoal animate-pulse" />
            </div>

            <div className="text-left hidden sm:block">
              <div className="text-xs font-bold text-ivory flex items-center gap-1.5">
                <span>Lashary Support Desk</span>
              </div>
              <div className="text-[10px] font-mono text-muted">
                Online · Founder Direct Assistance
              </div>
            </div>
          </button>
        )}
      </aside>

      {/* Floating Chat Modal */}
      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Lashary Technical Support Desk"
          className="fixed bottom-16 sm:bottom-6 right-4 sm:right-6 z-50 w-[92vw] sm:w-[420px] h-[540px] max-h-[78vh] rounded-3xl bg-charcoal/95 border border-white/10 shadow-2xl backdrop-blur-2xl flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-5 duration-300"
        >
          {/* Header */}
          <div className="px-5 py-4 bg-plum-black/90 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-9 h-9 rounded-2xl bg-gradient-to-r from-rose to-vermilion flex items-center justify-center text-white shadow-md">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-jade border-2 border-charcoal animate-pulse" />
              </div>
              <div>
                <div className="text-sm font-bold text-ivory flex items-center gap-1.5">
                  <span>Lashary Support Desk</span>
                  <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded bg-rose/15 text-rose border border-rose/30">
                    v1.5.0 Desk
                  </span>
                </div>
                <div className="text-[11px] text-muted flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-jade inline-block" />
                  <span>Direct Founder & Engineering Help</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              {/* API Key settings button */}
              <button
                onClick={() => setShowApiKeyModal(!showApiKeyModal)}
                title="Configure Gemini API Key"
                className={`p-2 rounded-xl text-xs transition-all ${
                  geminiApiKey
                    ? 'text-jade bg-jade/10 hover:bg-jade/20'
                    : 'text-apricot bg-apricot/10 hover:bg-apricot/20'
                }`}
              >
                <Key className="w-4 h-4" />
              </button>

              {/* Close button */}
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-xl text-muted hover:text-ivory hover:bg-white/5 transition-all"
                aria-label="Close Support Chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* API Key Drawer Dropdown */}
          {showApiKeyModal && (
            <div className="p-4 bg-plum-black border-b border-rose/20 space-y-3 text-xs">
              <div className="flex items-center justify-between">
                <span className="font-bold text-ivory flex items-center gap-1.5">
                  <Key className="w-3.5 h-3.5 text-rose" />
                  Google AI Studio Gemini API Key
                </span>
                <a
                  href="https://aistudio.google.com/app/apikey"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-rose hover:text-apricot hover:underline flex items-center gap-1 text-[11px]"
                >
                  Get Key <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <p className="text-[11px] text-muted">
                Paste your Gemini API key below to enable direct generative intelligence. Your key is stored strictly in your browser.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="password"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder="AIzaSy..."
                  className="flex-1 px-3 py-1.5 rounded-xl bg-obsidian border border-white/10 text-ivory font-mono text-xs focus:outline-none focus:border-rose"
                />
                <button
                  onClick={handleSaveApiKey}
                  className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-rose to-vermilion text-white font-bold hover:opacity-90 transition-all text-xs"
                >
                  Save
                </button>
              </div>
              {geminiApiKey && (
                <div className="text-[11px] text-jade flex items-center gap-1 font-mono">
                  <CheckCircle2 className="w-3 h-3" /> Active key connected
                </div>
              )}
            </div>
          )}

          {/* Messages Stream */}
          <div className="flex-1 p-4 overflow-y-auto space-y-4 text-xs">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex gap-2.5 ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {m.sender === 'assistant' && (
                  <div className="w-7 h-7 rounded-xl bg-plum-black border border-white/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Bot className="w-3.5 h-3.5 text-rose" />
                  </div>
                )}
                <div
                  className={`max-w-[82%] p-3.5 rounded-2xl leading-relaxed whitespace-pre-wrap ${
                    m.sender === 'user'
                      ? 'bg-gradient-to-r from-rose to-vermilion text-white font-medium rounded-tr-none'
                      : 'bg-plum-black/80 border border-white/10 text-soft-white rounded-tl-none shadow-md'
                  }`}
                >
                  {m.text}
                  <div
                    className={`mt-1.5 text-[9px] font-mono ${
                      m.sender === 'user' ? 'text-white/70 text-right' : 'text-muted'
                    }`}
                  >
                    {m.timestamp}
                  </div>
                </div>
                {m.sender === 'user' && (
                  <div className="w-7 h-7 rounded-xl bg-rose/20 border border-rose/40 flex items-center justify-center shrink-0 mt-0.5">
                    <User className="w-3.5 h-3.5 text-rose" />
                  </div>
                )}
              </div>
            ))}

            {isLoading && (
              <div className="flex gap-2.5 items-center">
                <div className="w-7 h-7 rounded-xl bg-plum-black border border-white/10 flex items-center justify-center shrink-0">
                  <Bot className="w-3.5 h-3.5 text-rose" />
                </div>
                <div className="p-3 rounded-2xl bg-plum-black/80 border border-white/10 rounded-tl-none flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-rose animate-bounce" />
                  <span className="w-2 h-2 rounded-full bg-rose animate-bounce [animation-delay:0.2s]" />
                  <span className="w-2 h-2 rounded-full bg-rose animate-bounce [animation-delay:0.4s]" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Suggestion Chips */}
          <div className="px-3 py-2 bg-plum-black/60 border-t border-white/5 flex items-center gap-1.5 overflow-x-auto scrollbar-none">
            {QUICK_CHIPS.map((chip) => (
              <button
                key={chip.label}
                onClick={() => handleSendMessage(chip.query)}
                className="shrink-0 px-2.5 py-1 rounded-lg bg-charcoal/80 hover:bg-wine/40 border border-white/10 text-[11px] text-muted hover:text-ivory hover:border-rose/30 transition-all"
              >
                {chip.label}
              </button>
            ))}
          </div>

          {/* Input Box */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
            className="p-3 bg-plum-black border-t border-white/10 flex items-center gap-2"
          >
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Ask about pricing, safety, setups..."
              className="flex-1 px-4 py-2.5 rounded-xl bg-obsidian border border-white/10 text-ivory text-xs placeholder:text-muted focus:outline-none focus:border-rose transition-all"
            />
            <button
              type="submit"
              disabled={!inputMessage.trim() || isLoading}
              className="p-2.5 rounded-xl bg-gradient-to-r from-rose to-vermilion text-white font-bold hover:opacity-90 disabled:opacity-40 disabled:pointer-events-none transition-all cursor-pointer"
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default AiSupportWidget;
